import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { config } from '../constants/config';
import { refreshPathDefsCache } from '../utils/pathRegistryUtils';

/**
 * Fetch all path definitions
 */
export const fetchPathDefinitions = createAsyncThunk(
  'pathRegistry/fetchPathDefinitions',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(config.API_PATH_DEFINITIONS, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to fetch path definitions' });
    }
  }
);

/**
 * Fetch a single path definition by name
 */
export const fetchPathDefinition = createAsyncThunk(
  'pathRegistry/fetchPathDefinition',
  async (name, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${config.API_PATH_DEFINITIONS}/${name}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: `Failed to fetch path definition: ${name}` });
    }
  }
);

/**
 * Create a new path definition
 */
export const createPathDefinition = createAsyncThunk(
  'pathRegistry/createPathDefinition',
  async (pathDef, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(config.API_PATH_DEFINITIONS, pathDef, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to create path definition' });
    }
  }
);

/**
 * Update an existing path definition
 */
export const updatePathDefinition = createAsyncThunk(
  'pathRegistry/updatePathDefinition',
  async ({ name, updates }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.put(`${config.API_PATH_DEFINITIONS}/${name}`, updates, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: `Failed to update path definition: ${name}` });
    }
  }
);

/**
 * Delete a path definition
 */
export const deletePathDefinition = createAsyncThunk(
  'pathRegistry/deletePathDefinition',
  async (name, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${config.API_PATH_DEFINITIONS}/${name}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return name;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: `Failed to delete path definition: ${name}` });
    }
  }
);

/**
 * Test path resolution
 */
export const testPathResolution = createAsyncThunk(
  'pathRegistry/testPathResolution',
  async ({ userId, path }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(config.API_TEST_PATH_RESOLUTION,
        { userId, path },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to test path resolution' });
    }
  }
);

/**
 * Create full backup (local + S3)
 */
export const createFullBackup = createAsyncThunk(
  'pathRegistry/createFullBackup',
  async (bucketName, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${config.BASE_URL}/path-registry/backup-full`,
        { bucketName: bucketName || 'authorator-ai-backups' },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to create backup' });
    }
  }
);

/**
 * Path Registry Slice
 */
const pathRegistrySlice = createSlice({
  name: 'pathRegistry',
  initialState: {
    pathDefinitions: [],
    currentPathDefinition: null,
    testResult: null,
    loading: false,
    error: null,
    success: null
  },
  reducers: {
    clearCurrentPathDefinition: (state) => {
      state.currentPathDefinition = null;
    },
    clearTestResult: (state) => {
      state.testResult = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch all path definitions
    builder
      .addCase(fetchPathDefinitions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPathDefinitions.fulfilled, (state, action) => {
        state.loading = false;
        state.pathDefinitions = action.payload;
      })
      .addCase(fetchPathDefinitions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Failed to fetch path definitions';
      });

    // Fetch a single path definition
    builder
      .addCase(fetchPathDefinition.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPathDefinition.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPathDefinition = action.payload;
      })
      .addCase(fetchPathDefinition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Failed to fetch path definition';
      });

    // Create a new path definition
    builder
      .addCase(createPathDefinition.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createPathDefinition.fulfilled, (state, action) => {
        state.loading = false;
        state.pathDefinitions.push(action.payload);
        state.success = 'Path definition created successfully';
        // Refresh the client-side cache
        refreshPathDefsCache().catch(err => console.error('Error refreshing path definitions cache:', err));
      })
      .addCase(createPathDefinition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Failed to create path definition';
      });

    // Update an existing path definition
    builder
      .addCase(updatePathDefinition.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updatePathDefinition.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.pathDefinitions.findIndex(def => def.name === action.payload.name);
        if (index !== -1) {
          state.pathDefinitions[index] = action.payload;
        }
        state.currentPathDefinition = action.payload;
        state.success = 'Path definition updated successfully';
        // Refresh the client-side cache
        refreshPathDefsCache().catch(err => console.error('Error refreshing path definitions cache:', err));
      })
      .addCase(updatePathDefinition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Failed to update path definition';
      });

    // Delete a path definition
    builder
      .addCase(deletePathDefinition.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deletePathDefinition.fulfilled, (state, action) => {
        state.loading = false;
        state.pathDefinitions = state.pathDefinitions.filter(def => def.name !== action.payload);
        if (state.currentPathDefinition && state.currentPathDefinition.name === action.payload) {
          state.currentPathDefinition = null;
        }
        state.success = 'Path definition deleted successfully';
        // Refresh the client-side cache
        refreshPathDefsCache().catch(err => console.error('Error refreshing path definitions cache:', err));
      })
      .addCase(deletePathDefinition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Failed to delete path definition';
      });

    // Test path resolution
    builder
      .addCase(testPathResolution.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.testResult = null;
      })
      .addCase(testPathResolution.fulfilled, (state, action) => {
        state.loading = false;
        state.testResult = action.payload;
      })
      .addCase(testPathResolution.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Failed to test path resolution';
      });

    // Create full backup
    builder
      .addCase(createFullBackup.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createFullBackup.fulfilled, (state, action) => {
        state.loading = false;
        const backupInfo = action.payload;
        let successMessage = 'Path Registry backup completed successfully!';
        if (backupInfo.s3 && backupInfo.s3.location) {
          successMessage += ` Backed up to: ${backupInfo.s3.location}`;
        }
        state.success = successMessage;
      })
      .addCase(createFullBackup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || 'Failed to create backup';
      });
  }
});

export const {
  clearCurrentPathDefinition,
  clearTestResult,
  clearError,
  clearSuccess
} = pathRegistrySlice.actions;

export default pathRegistrySlice.reducer;
